import { useNavigate } from "react-router-dom";
import {
  MessageSquare,
  Clock,
  CheckCircle,
  Users,
  ArrowRight,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useInboxes } from "@/hooks/use-inboxes";
import { cn } from "@/lib/utils";
import type { Inbox } from "@/types/inbox";

interface MetricCardProps {
  title: string;
  value: number;
  icon: React.ElementType;
  accentClass: string;
  loading: boolean;
}

function MetricCard({
  title,
  value,
  icon: Icon,
  accentClass,
  loading,
}: MetricCardProps) {
  return (
    <Card size="sm">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "flex h-10 w-10 items-center justify-center rounded-lg",
              accentClass,
            )}
          >
            <Icon className="h-5 w-5" />
          </div>
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {title}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-9 w-20" />
        ) : (
          <span className="text-3xl font-bold tracking-tight">{value}</span>
        )}
      </CardContent>
    </Card>
  );
}

const statusDotColor: Record<string, string> = {
  open: "bg-green-500",
  pending: "bg-amber-500",
  resolved: "bg-slate-400",
};

function getInitials(name?: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
}

function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength) + "...";
}

function relativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return "agora";
  if (diffMinutes < 60) return `${diffMinutes}min`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths}m`;
}

function RecentConversationItem({ inbox }: { inbox: Inbox }) {
  const navigate = useNavigate();
  const contactName = inbox.contact_name || `Contato #${inbox.id_contact}`;
  const lastMessage = inbox.last_message_content
    ? truncate(inbox.last_message_content, 60)
    : "Sem mensagens";

  return (
    <button
      onClick={() => navigate(`/inbox/${inbox.id}`)}
      className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors hover:bg-accent"
    >
      <div className="relative">
        <Avatar size="lg">
          <AvatarFallback className="text-xs">
            {getInitials(contactName)}
          </AvatarFallback>
        </Avatar>
        <span
          className={cn(
            "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background",
            statusDotColor[inbox.status] || "bg-slate-400",
          )}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between">
          <span className="truncate text-sm font-medium">{contactName}</span>
          <span className="shrink-0 text-xs text-muted-foreground">
            {relativeTime(inbox.last_message_at || inbox.datetime_add)}
          </span>
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {lastMessage}
        </p>
      </div>
    </button>
  );
}

export function DashboardPage() {
  const { inboxes, loading: inboxesLoading } = useInboxes();
  const navigate = useNavigate();

  const openCount = inboxes.filter((i) => i.status === "open").length;
  const pendingCount = inboxes.filter((i) => i.status === "pending").length;
  const resolvedCount = inboxes.filter((i) => i.status === "resolved").length;

  const recentInboxes = [...inboxes]
    .sort(
      (a, b) =>
        new Date(b.last_message_at || b.datetime_add).getTime() -
        new Date(a.last_message_at || a.datetime_add).getTime(),
    )
    .slice(0, 5);

  return (
    <div className="h-full overflow-auto mx-auto max-w-6xl space-y-8 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Visão geral das suas conversas
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard
          title="Abertas"
          value={openCount}
          icon={MessageSquare}
          accentClass="bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400"
          loading={inboxesLoading}
        />
        <MetricCard
          title="Pendentes"
          value={pendingCount}
          icon={Clock}
          accentClass="bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400"
          loading={inboxesLoading}
        />
        <MetricCard
          title="Resolvidas"
          value={resolvedCount}
          icon={CheckCircle}
          accentClass="bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-400"
          loading={inboxesLoading}
        />
        <MetricCard
          title="Total"
          value={inboxes.length}
          icon={Users}
          accentClass="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
          loading={inboxesLoading}
        />
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Conversas Recentes</h2>
        </div>

        <Card>
          <CardContent className="p-0">
            {inboxesLoading ? (
              <div className="space-y-2 p-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-14 w-full rounded-lg" />
                ))}
              </div>
            ) : recentInboxes.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-12 text-muted-foreground">
                <MessageSquare className="h-10 w-10 opacity-30" />
                <p className="text-sm">Nenhuma conversa encontrada</p>
              </div>
            ) : (
              <div className="divide-y">
                {recentInboxes.map((inbox) => (
                  <RecentConversationItem key={inbox.id} inbox={inbox} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button onClick={() => navigate("/inbox")} variant="default">
          <MessageSquare className="mr-2 h-4 w-4" />
          Ir para Inbox
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <Button onClick={() => navigate("/channels")} variant="outline">
          <Users className="mr-2 h-4 w-4" />
          Canais
        </Button>
      </div>
    </div>
  );
}
